import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ContractApiError, contractApi } from '@/api/commercial/contracts';
import type { Proposal } from '@/api/commercial/proposals';
import type {
  ContractListParams,
  CreateContractPayload,
  UpdateContractPayload,
} from '@/types/commercial/contracts';

const contractQueryKeys = {
  all: ['contracts'] as const,
  lists: () => [...contractQueryKeys.all, 'list'] as const,
  list: (params: ContractListParams) => [...contractQueryKeys.lists(), params] as const,
  details: () => [...contractQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...contractQueryKeys.details(), id] as const,
  versions: (id: string) => [...contractQueryKeys.detail(id), 'versions'] as const,
  versionList: (id: string, page: number, limit: number) =>
    [...contractQueryKeys.versions(id), { page, limit }] as const,
  version: (id: string, version: number) => [...contractQueryKeys.versions(id), version] as const,
  proposal: (proposalId: string) => [...contractQueryKeys.all, 'proposal', proposalId] as const,
};

export function useContracts(params: ContractListParams, enabled = true) {
  return useQuery({
    queryKey: contractQueryKeys.list(params),
    queryFn: () => contractApi.findMany(params),
    enabled,
    staleTime: 30_000,
  });
}

export function useContractVersions(id: string | null, page = 1, limit = 10, enabled = true) {
  return useQuery({
    queryKey: contractQueryKeys.versionList(id ?? '', page, limit),
    queryFn: () => contractApi.findVersions(id ?? '', { page, limit }),
    enabled: Boolean(id) && enabled,
  });
}

export function useContractVersion(id: string | null, version: number | null) {
  return useQuery({
    queryKey: contractQueryKeys.version(id ?? '', version ?? 0),
    queryFn: () => contractApi.findVersion(id ?? '', version ?? 0),
    enabled: Boolean(id) && version !== null,
  });
}

function useContractInvalidation() {
  const queryClient = useQueryClient();
  return {
    lists: () => queryClient.invalidateQueries({ queryKey: contractQueryKeys.lists() }),
    detail: (id: string) =>
      queryClient.invalidateQueries({ queryKey: contractQueryKeys.detail(id) }),
    versions: (id: string) =>
      queryClient.invalidateQueries({ queryKey: contractQueryKeys.versions(id) }),
  };
}

export function useCreateContract() {
  const invalidate = useContractInvalidation();
  return useMutation({ mutationFn: contractApi.create, onSuccess: invalidate.lists });
}

export function useUpdateContract() {
  const invalidate = useContractInvalidation();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateContractPayload }) =>
      contractApi.update(id, payload),
    onSuccess: async (contract) => {
      await Promise.all([
        invalidate.lists(),
        invalidate.detail(contract.id),
        invalidate.versions(contract.id),
      ]);
    },
  });
}

export function useActivateContract() {
  const invalidate = useContractInvalidation();
  return useMutation({
    mutationFn: contractApi.activate,
    onSuccess: async (contract) => {
      await Promise.all([invalidate.lists(), invalidate.detail(contract.id)]);
    },
  });
}

export function useInactivateContract() {
  const invalidate = useContractInvalidation();
  return useMutation({
    mutationFn: contractApi.inactivate,
    onSuccess: async (contract) => {
      await Promise.all([invalidate.lists(), invalidate.detail(contract.id)]);
    },
  });
}

export function useCreateContractVersion() {
  const invalidate = useContractInvalidation();
  return useMutation({
    mutationFn: contractApi.createVersion,
    onSuccess: async (version) => {
      await Promise.all([
        invalidate.lists(),
        invalidate.detail(version.contractId),
        invalidate.versions(version.contractId),
      ]);
    },
  });
}

export function useProposalContracts(proposalId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: contractQueryKeys.proposal(proposalId ?? ''),
    queryFn: () => contractApi.findByProposal(proposalId ?? ''),
    enabled: Boolean(proposalId) && enabled,
  });
}

export function useSignedProposalContracts(proposals: Proposal[]) {
  const results = useQueries({
    queries: proposals.map((proposal) => ({
      queryKey: [...contractQueryKeys.proposal(proposal.id), 'signed'] as const,
      queryFn: async () => {
        const contracts = await contractApi.findByProposal(proposal.id);
        const signedContracts = await Promise.all(
          contracts.map(async (contract) => {
            const version = await contractApi.findVersion(contract.id, contract.version);
            try {
              const signature = await contractApi.findSignatureStatus(version.id);
              return signature.status === 'signed' ? contract : null;
            } catch (error) {
              if (
                error instanceof ContractApiError &&
                error.code === 'SIGNATURE_PROCESS_NOT_FOUND'
              ) {
                return null;
              }
              throw error;
            }
          }),
        );
        return signedContracts.filter((contract) => contract !== null);
      },
      staleTime: 30_000,
    })),
  });

  return new Map(proposals.map((proposal, index) => [proposal.id, results[index]]));
}

export function useSyncProposalContracts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: contractApi.syncProposal,
    onSuccess: (contracts, proposalId) => {
      queryClient.setQueryData(contractQueryKeys.proposal(proposalId), contracts);
    },
  });
}

export type { CreateContractPayload };
